import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import FormInput from '../Form/FormInput';
import FormToggle from '../Form/FormToggle';
import { HiPlus, HiTrash, HiUpload } from 'react-icons/hi';
import styles from './BulkImportModal.module.css';

export interface BulkImportRow {
  name: string;
  lastname: string;
  email: string;
  ticketId: string;
  __status?: 'pending' | 'fulfilled' | 'rejected' | 'invalid';
}

export interface Ticket {
  ticketID: string;
  title: string;
  price?: number | string;
  currency?: string;
}

export interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventID: string;
  channelID: string;
  tickets: Ticket[];
  existingEmails: string[];
  token: string;
  onImportSuccess: () => void;
  getTranslation: (key: string, params?: Record<string, string | number>) => string;
  toastSuccess: (message: string) => void;
  toastError: (message: string) => void;
  createSoldTicket: (
    token: string,
    channelID: string,
    eventID: string,
    ticketID: string,
    paymentID: string,
    name: string,
    lastname: string,
    email: string,
    phone: string,
    isPaid: boolean,
    billing: Record<string, string>
  ) => Promise<{ data: { status: boolean } }>;
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  eventID,
  channelID,
  tickets,
  existingEmails,
  token,
  onImportSuccess,
  getTranslation,
  toastSuccess,
  toastError,
  createSoldTicket,
}) => {
  // State
  const [rows, setRows] = useState<BulkImportRow[]>([
    { name: '', lastname: '', email: '', ticketId: '' },
  ]);
  const [soldEmails, setSoldEmails] = useState<Set<string>>(new Set());
  const [defaultTicketId, setDefaultTicketId] = useState<string>('');
  const [defaultIsPaid, setDefaultIsPaid] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize sold emails and default ticket
  useEffect(() => {
    if (isOpen) {
      setSoldEmails(new Set(existingEmails.map((e) => e.toLowerCase())));
      if (tickets.length > 0 && !defaultTicketId) {
        setDefaultTicketId(tickets[0].ticketID);
      }
      // Reset state when modal opens
      setRows([{ name: '', lastname: '', email: '', ticketId: tickets[0]?.ticketID || '' }]);
      setShowErrors(false);
    }
  }, [isOpen, existingEmails, tickets, defaultTicketId]);

  // Helpers
  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, []);

  const normalizeSpace = (s: string): string => String(s || '').replace(/\s+/g, ' ').trim();

  const splitFullName = (full: string): { name: string; lastname: string } => {
    const f = normalizeSpace(full);
    if (!f) return { name: '', lastname: '' };
    const parts = f.split(' ').filter(Boolean);
    if (parts.length === 1) return { name: parts[0], lastname: '' };
    const lastname = parts.pop() || '';
    const name = parts.join(' ');
    return { name, lastname };
  };

  const isEmailAlreadySold = (email: string): boolean => {
    return email ? soldEmails.has(email.toLowerCase()) : false;
  };

  const isValidRow = (row: BulkImportRow): boolean => {
    return (
      row.name?.trim().length > 0 &&
      row.lastname?.trim().length > 0 &&
      emailRegex.test(row.email || '')
    );
  };

  const ticketOptions = useMemo(
    () =>
      tickets.map((t) => ({
        label: `${t.title}${t.price ? ` — ${t.price} ${t.currency || ''}` : ''}`,
        value: t.ticketID,
      })),
    [tickets]
  );

  const validTicketIds = useMemo(
    () => new Set(ticketOptions.map((o) => String(o.value))),
    [ticketOptions]
  );

  const labelToId = useMemo(() => {
    const m = new Map<string, string>();
    ticketOptions.forEach((o) => m.set(o.label.toLowerCase(), String(o.value)));
    return m;
  }, [ticketOptions]);

  const resolveTicket = (raw: string): string => {
    if (!raw) return '';
    const s = String(raw).trim();
    if (!s) return '';
    if (validTicketIds.has(s)) return s;
    const byLabel = labelToId.get(s.toLowerCase());
    if (byLabel) return byLabel;
    for (const [label, id] of Array.from(labelToId.entries())) {
      if (label.startsWith(s.toLowerCase())) return id;
    }
    return '';
  };

  // Header detection patterns
  const HEADER_PATTERNS = useMemo(() => {
    const or = (arr: string[]) => new RegExp(`^(?:${arr.join('|')})$`, 'i');
    const NAME = or(['ad', 'adı', 'isim', 'first ?name', 'vorname', 'prenom', 'prénom', 'nombre', 'nome']);
    const LASTNAME = or(['soyad', 'soyadı', 'last ?name', 'nachname', 'familienname', 'apellido', 'cognome', 'nom de famille']);
    const FULLNAME = or(['ad soyad', 'adı soyadı', 'isim soyisim', 'full ?name', 'name', 'nome e cognome', 'nombre completo', 'nom complet']);
    const EMAIL = or(['e-?posta', 'eposta', 'email', 'e-mail', 'mail', 'correo', 'mailadresse']);
    const TICKET = or(['ticket ?id', 'ticket', 'bilet', 'billet', 'entrada', 'biglietto']);
    return { NAME, LASTNAME, FULLNAME, EMAIL, TICKET };
  }, []);

  const detectHeaderMap = (headerRow: string[] = []): { name: number; lastname: number; fullName: number; email: number; ticketId: number } | null => {
    const normalize = (s: string) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const map = { name: -1, lastname: -1, fullName: -1, email: -1, ticketId: -1 };
    headerRow.forEach((cell, idx) => {
      const h = normalize(cell);
      if (HEADER_PATTERNS.FULLNAME.test(h)) map.fullName = idx;
      else if (HEADER_PATTERNS.NAME.test(h)) map.name = idx;
      else if (HEADER_PATTERNS.LASTNAME.test(h)) map.lastname = idx;
      else if (HEADER_PATTERNS.EMAIL.test(h)) map.email = idx;
      else if (HEADER_PATTERNS.TICKET.test(h)) map.ticketId = idx;
    });
    const matchCount = Object.values(map).filter((v) => v >= 0).length;
    return matchCount >= 2 ? map : null;
  };

  const guessMapFromRow = (row: string[] = []): { name: number; lastname: number; fullName: number; email: number; ticketId: number } => {
    let emailIdx = row.findIndex((c) => emailRegex.test(String(c || '').trim()));
    if (emailIdx === -1) emailIdx = 2;
    let ticketIdx = -1;
    row.forEach((c, i) => {
      const s = String(c || '').trim();
      if (resolveTicket(s)) ticketIdx = i;
    });
    let nameIdx = -1, lastnameIdx = -1, fullNameIdx = -1;
    const first = normalizeSpace(row[0] || '');
    const second = normalizeSpace(row[1] || '');
    const hasSpaceFirst = first.includes(' ');
    const hasSpaceSecond = second.includes(' ');
    if (hasSpaceFirst && emailIdx !== 0) {
      fullNameIdx = 0;
    } else if (hasSpaceSecond && emailIdx !== 1) {
      fullNameIdx = 1;
    } else {
      if (row.length >= 2) {
        nameIdx = 0;
        if (emailIdx !== 1) lastnameIdx = 1;
      } else if (row.length === 1 && !emailRegex.test(first)) {
        fullNameIdx = 0;
      }
    }
    return { name: nameIdx, lastname: lastnameIdx, fullName: fullNameIdx, email: emailIdx, ticketId: ticketIdx };
  };

  // Row management
  const updateRow = (index: number, field: keyof BulkImportRow, value: string) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value, __status: undefined };
      return updated;
    });
  };

  const addEmptyRow = () => {
    setRows((prev) => [...prev, { name: '', lastname: '', email: '', ticketId: defaultTicketId }]);
  };

  const deleteRow = (index: number) => {
    setRows((prev) => {
      if (prev.length === 1) {
        return [{ name: '', lastname: '', email: '', ticketId: defaultTicketId }];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearAll = () => {
    setRows([{ name: '', lastname: '', email: '', ticketId: defaultTicketId }]);
    setShowErrors(false);
  };

  const applyTicketToAll = () => {
    setRows((prev) => prev.map((row) => ({ ...row, ticketId: defaultTicketId })));
  };

  // Excel/CSV import
  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const matrix: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });

      if (!matrix || matrix.length === 0) return;

      let body = matrix;
      let map = null;

      const headerMap = detectHeaderMap(matrix[0]);
      if (headerMap) {
        body = matrix.slice(1);
        map = headerMap;
      } else {
        const probe = matrix.find((r) => (r || []).some((c) => String(c || '').trim().length > 0)) || [];
        map = guessMapFromRow(probe);
      }

      const imported: BulkImportRow[] = [];
      for (const r of body) {
        const get = (i: number) => (i >= 0 ? r[i] : '');
        let name = '', lastname = '', email = '', ticketId = '';

        const rawFull = get(map.fullName);
        const rawName = get(map.name);
        const rawLastname = get(map.lastname);
        const rawEmail = get(map.email);
        const rawTicket = get(map.ticketId);

        if (rawEmail) email = normalizeSpace(rawEmail);

        if (rawFull) {
          const sp = splitFullName(rawFull);
          name = sp.name;
          lastname = sp.lastname;
        } else {
          const n = normalizeSpace(rawName);
          const l = normalizeSpace(rawLastname);
          if (n && !l && n.includes(' ')) {
            const sp = splitFullName(n);
            name = sp.name;
            lastname = sp.lastname;
          } else {
            name = n;
            lastname = l;
          }
        }

        ticketId = resolveTicket(rawTicket) || defaultTicketId;

        if (name || lastname || email || ticketId) {
          imported.push({ name, lastname, email, ticketId });
        }
      }

      if (imported.length === 0) return;

      // Replace empty first row or append
      setRows((prev) => {
        const isEmpty = prev.length === 1 && !prev[0].name && !prev[0].lastname && !prev[0].email;
        if (isEmpty) {
          return imported;
        }
        return [...prev, ...imported];
      });
    } catch (error) {
      toastError(getTranslation('organizerPanel.importError'));
    } finally {
      e.target.value = '';
    }
  };

  // Save all rows
  const saveAll = async () => {
    setShowErrors(true);
    setSaving(true);

    try {
      const results: { status: 'fulfilled' | 'rejected' | 'invalid' }[] = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        if (!isValidRow(row)) {
          results.push({ status: 'invalid' });
          continue;
        }

        try {
          const billing = { tckn: '', address: '', city: '', region: '', postalCode: '' };
          await createSoldTicket(
            token,
            channelID,
            eventID,
            row.ticketId || '',
            'bulk-entry',
            row.name,
            row.lastname,
            row.email,
            '1111111111',
            defaultIsPaid,
            billing
          );
          results.push({ status: 'fulfilled' });
        } catch {
          results.push({ status: 'rejected' });
        }
      }

      // Update rows with status
      const processed = rows.map((row, idx) => ({
        ...row,
        __status: results[idx]?.status,
      }));

      const remaining = processed.filter((r) => r.__status !== 'fulfilled');

      const nextRows =
        remaining.length > 0
          ? remaining
          : [{ name: '', lastname: '', email: '', ticketId: defaultTicketId }];

      setRows(nextRows);

      // Update sold emails
      const newSold = new Set(soldEmails);
      rows.forEach((row, idx) => {
        if (results[idx]?.status === 'fulfilled' && row.email) {
          newSold.add(row.email.toLowerCase());
        }
      });
      setSoldEmails(newSold);

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.filter((r) => r.status !== 'fulfilled').length;

      if (successCount > 0 && failCount === 0) {
        toastSuccess(
          getTranslation('organizerPanel.importSuccess', { count: String(successCount) })
        );
        onImportSuccess();
        onClose();
      } else if (successCount > 0 && failCount > 0) {
        toastSuccess(
          getTranslation('organizerPanel.importPartialSuccess', {
            successCount: String(successCount),
            failCount: String(failCount),
          })
        );
        onImportSuccess();
      } else {
        toastError(getTranslation('organizerPanel.importError'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Get row error state
  const getRowError = (row: BulkImportRow): 'missing' | 'invalid-email' | 'duplicate' | null => {
    if (!row.name?.trim() || !row.lastname?.trim()) return 'missing';
    if (row.email && !emailRegex.test(row.email)) return 'invalid-email';
    if (row.email && isEmailAlreadySold(row.email)) return 'duplicate';
    if (!row.email?.trim()) return 'missing';
    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getTranslation('organizerPanel.bulkImportTitle')}
      className={styles.bulkImportModal}
    >
      <div className={styles.content}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <div className={styles.ticketSelector}>
              <select
                value={defaultTicketId}
                onChange={(e) => setDefaultTicketId(e.target.value)}
                className={styles.ticketSelect}
                disabled={tickets.length === 0}
              >
                {tickets.length === 0 ? (
                  <option value="">{getTranslation('organizerPanel.noTicketsAvailable')}</option>
                ) : (
                  tickets.map((t) => (
                    <option key={t.ticketID} value={t.ticketID}>
                      {t.title}{t.price ? ` — ${t.price} ${t.currency || ''}` : ''}
                    </option>
                  ))
                )}
              </select>
              <Button
                variant="secondary"
                size="small"
                onClick={applyTicketToAll}
                disabled={tickets.length === 0}
              >
                {getTranslation('organizerPanel.applyToAll')}
              </Button>
            </div>
            <div className={styles.paidToggle}>
              <FormToggle
                checked={defaultIsPaid}
                onChange={(checked) => setDefaultIsPaid(checked)}
              />
              <span className={styles.toggleLabel}>
                {getTranslation('organizerPanel.paymentDone')}
              </span>
            </div>
          </div>
          <div className={styles.toolbarRight}>
            <Button
              variant="secondary"
              size="small"
              onClick={() => fileInputRef.current?.click()}
            >
              {/* @ts-ignore */}
              <HiUpload size={16} />
              {getTranslation('organizerPanel.importFromExcel')}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              onChange={handleFileImport}
              style={{ display: 'none' }}
            />
            <Button variant="secondary" size="small" onClick={addEmptyRow}>
              {/* @ts-ignore */}
              <HiPlus size={16} />
              {getTranslation('organizerPanel.addRow')}
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendMissing}`}></span>
            {getTranslation('organizerPanel.missingRequiredField')}
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendInvalid}`}></span>
            {getTranslation('organizerPanel.invalidEmail')}
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDuplicate}`}></span>
            {getTranslation('organizerPanel.emailAlreadyTaken')}
          </div>
        </div>

        {/* Data Grid */}
        <div className={styles.gridContainer}>
          <div className={styles.gridHeader}>
            <div className={styles.gridCell} style={{ width: '40px' }}>#</div>
            <div className={styles.gridCell} style={{ flex: 1 }}>{getTranslation('organizerPanel.name')}</div>
            <div className={styles.gridCell} style={{ flex: 1 }}>{getTranslation('organizerPanel.lastname')}</div>
            <div className={styles.gridCell} style={{ flex: 1.5 }}>{getTranslation('organizerPanel.email')}</div>
            <div className={styles.gridCell} style={{ flex: 1 }}>{getTranslation('organizerPanel.ticketType')}</div>
            <div className={styles.gridCell} style={{ width: '40px' }}></div>
          </div>
          <div className={styles.gridBody}>
            {rows.map((row, index) => {
              const error = showErrors ? getRowError(row) : null;
              const statusClass = row.__status === 'rejected' || row.__status === 'invalid' ? styles.rowError : '';
              const errorClass = error === 'missing' ? styles.rowMissing :
                                 error === 'invalid-email' ? styles.rowInvalidEmail :
                                 error === 'duplicate' ? styles.rowDuplicate : '';

              return (
                <div key={index} className={`${styles.gridRow} ${statusClass} ${errorClass}`}>
                  <div className={styles.gridCell} style={{ width: '40px' }}>
                    <span className={styles.rowNumber}>{index + 1}</span>
                  </div>
                  <div className={styles.gridCell} style={{ flex: 1 }}>
                    <FormInput
                      type="text"
                      value={row.name}
                      onChange={(e) => updateRow(index, 'name', e.target.value)}
                      placeholder={getTranslation('organizerPanel.name')}
                      error={showErrors && !row.name?.trim()}
                      className={styles.gridInput}
                    />
                  </div>
                  <div className={styles.gridCell} style={{ flex: 1 }}>
                    <FormInput
                      type="text"
                      value={row.lastname}
                      onChange={(e) => updateRow(index, 'lastname', e.target.value)}
                      placeholder={getTranslation('organizerPanel.lastname')}
                      error={showErrors && !row.lastname?.trim()}
                      className={styles.gridInput}
                    />
                  </div>
                  <div className={styles.gridCell} style={{ flex: 1.5 }}>
                    <FormInput
                      type="email"
                      value={row.email}
                      onChange={(e) => updateRow(index, 'email', e.target.value)}
                      placeholder={getTranslation('organizerPanel.email')}
                      error={showErrors && (
                        !row.email?.trim() ||
                        !emailRegex.test(row.email) ||
                        isEmailAlreadySold(row.email)
                      )}
                      className={styles.gridInput}
                    />
                  </div>
                  <div className={styles.gridCell} style={{ flex: 1 }}>
                    <select
                      value={row.ticketId}
                      onChange={(e) => updateRow(index, 'ticketId', e.target.value)}
                      className={styles.ticketSelectSmall}
                    >
                      <option value="">{getTranslation('organizerPanel.selectTicketType')}</option>
                      {tickets.map((t) => (
                        <option key={t.ticketID} value={t.ticketID}>
                          {t.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.gridCell} style={{ width: '40px' }}>
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => deleteRow(index)}
                      aria-label="Delete row"
                    >
                      {/* @ts-ignore */}
                      <HiTrash size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <span className={styles.totalRows}>
            {getTranslation('organizerPanel.totalRows')}: {rows.length}
          </span>
          <div className={styles.footerActions}>
            <Button variant="secondary" size="medium" onClick={clearAll} disabled={saving}>
              {getTranslation('organizerPanel.clearAll')}
            </Button>
            <Button variant="primary" size="medium" onClick={saveAll} disabled={saving}>
              {saving ? getTranslation('organizerPanel.saving') : getTranslation('common.save')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BulkImportModal;
